<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Edit Audit </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <div class="mb-4">
              <v-text-field
                v-model="formData.checklist_name"
                :rules="[(v) => !!v || 'Checklist Name is required']"
                label="Audit Name"
                variant="outlined"
                density="compact"
                class="mb-4"
                required
              ></v-text-field>

              <v-autocomplete
                v-model="formData.checklist_type"
                :items="checklistTypeOptions"
                item-title="title"
                item-value="value"
                :rules="[(v) => !!v || 'Checklist Type is required']"
                label="Audit Type"
                variant="outlined"
                density="compact"
                class="mb-4"
                required
              ></v-autocomplete>

              <v-divider class="my-4"></v-divider>

              <div class="mb-4">
                <v-radio-group v-model="itemType" inline class="mb-4">
                  <v-radio
                    label="Line Item"
                    value="line_item"
                    color="primary"
                  ></v-radio>
                  <v-radio
                    label="Section"
                    value="section"
                    color="primary"
                  ></v-radio>
                </v-radio-group>

                <!-- Line Item Form -->
                <div v-if="itemType === 'line_item'" class="mb-4">
                  <v-text-field
                    v-model="lineItemName"
                    label="Line Item Name"
                    variant="outlined"
                    density="compact"
                    class="mb-2"
                    placeholder="Enter line item name"
                  ></v-text-field>
                  <v-btn
                    color="primary"
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    @click="addLineItem"
                    :disabled="!lineItemName"
                  >
                    Add Line Item
                  </v-btn>
                </div>

                <!-- Section Form -->
                <div v-if="itemType === 'section'" class="mb-4">
                  <v-text-field
                    v-model="sectionName"
                    label="Section Name"
                    variant="outlined"
                    density="compact"
                    class="mb-2"
                    placeholder="Enter section name"
                  ></v-text-field>

                  <!-- Add Items to Section (before adding section) -->
                  <div class="mb-4">
                    <v-text-field
                      v-model="tempSectionItemName"
                      label="Item Name"
                      variant="outlined"
                      density="compact"
                      class="mb-2"
                      placeholder="Enter item name"
                    ></v-text-field>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      size="small"
                      prepend-icon="mdi-plus"
                      @click="addTempSectionItem"
                      :disabled="!tempSectionItemName"
                    >
                      Add Item
                    </v-btn>
                  </div>

                  <!-- Display temporary section items -->
                  <div v-if="tempSectionItems.length > 0" class="mb-4">
                    <h4 class="text-subtitle-2 mb-2">Section Items:</h4>
                    <v-chip
                      v-for="(item, itemIndex) in tempSectionItems"
                      :key="`temp-item-${itemIndex}`"
                      class="ma-1"
                      closable
                      @click:close="removeTempSectionItem(itemIndex)"
                    >
                      {{ item.name }}
                    </v-chip>
                  </div>

                  <v-btn
                    color="primary"
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    @click="addSection"
                    :disabled="!sectionName || tempSectionItems.length === 0"
                  >
                    Add Section
                  </v-btn>
                </div>
              </div>

              <draggable
                v-model="checklistItems"
                item-key="id"
                handle=".drag-handle"
                animation="200"
              >
                <template #item="{ element, index }">
                  <div>
                    <!-- LINE ITEM -->

                    <v-card
                      v-if="element.type === 'line_item'"
                      class="mb-2"
                      variant="outlined"
                    >
                      <v-card-text
                        class="d-flex justify-space-between align-center"
                      >
                        <div class="d-flex align-center">
                          <v-icon class="mr-2 drag-handle"> mdi-drag </v-icon>

                          <v-text-field
                            v-if="editingLineItemIndex === index"
                            v-model="editingLineItemName"
                            density="compact"
                            variant="outlined"
                            style="width: 220px"
                            autofocus
                            @keyup.enter="saveLineItemEdit(index)"
                            @keyup.esc="cancelLineItemEdit"
                          />

                          <span v-else>
                            {{ element.name }}
                          </span>
                        </div>

                        <div>
                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="primary"
                            @click="editLineItem(index)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>

                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="error"
                            @click="removeLineItem(index)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>

                    <!-- SECTION -->

                    <v-card v-else class="mb-4" variant="outlined">
                      <v-card-title
                        class="d-flex justify-space-between align-center"
                      >
                        <div class="d-flex align-center">
                          <v-icon class="mr-2 drag-handle"> mdi-drag </v-icon>

                          <v-text-field
                            v-if="editingSectionIndex === index"
                            v-model="editingSectionName"
                            density="compact"
                            variant="outlined"
                            style="width: 220px"
                            autofocus
                            @keyup.enter="saveSectionEdit(index)"
                            @keyup.esc="cancelSectionEdit"
                          />

                          <span v-else class="text-subtitle-1">
                            {{ element.section_name }}
                          </span>
                        </div>

                        <div>
                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="primary"
                            @click="editSection(index)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>

                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="error"
                            @click="removeSection(index)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-card-title>

                      <v-card-text>
                        <div
                          v-for="(item, i) in element.items"
                          :key="i"
                          class="d-flex align-center mb-2"
                        >
                          <v-text-field
                            v-if="
                              editingSectionItemIndex === index &&
                              editingItemIndex === i
                            "
                            v-model="editingItemName"
                            density="compact"
                            variant="outlined"
                            style="width: 200px"
                            autofocus
                            @keyup.enter="saveSectionItemEdit(index, i)"
                            @keyup.esc="cancelSectionItemEdit"
                          />

                          <v-chip v-else class="ma-1">
                            {{ item.name }}
                          </v-chip>

                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="primary"
                            @click="editSectionItem(index, i)"
                          >
                            <v-icon size="small">mdi-pencil</v-icon>
                          </v-btn>

                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="error"
                            @click="removeItemFromSection(index, i)"
                          >
                            <v-icon size="small">mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>
                </template>
              </draggable>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="handleClose">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="
              !valid ||
              loading ||
              !checklistItems.length ||
              !allSectionsHaveItems
            "
            :loading="loading"
            @click="handleSubmit"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { edit_master_checklists } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

export default {
  name: "EditChecklistDialog",
  components: {
    SnackbarComponent,
    draggable,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    checklist: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "updated"],
  data() {
    return {
      valid: false,
      loading: false,
      formData: {
        checklist_name: "",
        checklist_type: "",
        checklist_source: "BMS",
      },
      itemType: "line_item",
      lineItemName: "",

      checklistItems: [],
      sectionName: "",

      tempSectionItemName: "",
      tempSectionItems: [],

      editingLineItemIndex: null,
      editingLineItemName: "",
      editingSectionIndex: null,
      editingSectionName: "",
      editingSectionItemIndex: null,
      editingItemIndex: null,
      editingItemName: "",
      checklistTypeOptions: [
        { title: "Med Pass Audit", value: "MED_PASS_AUDIT" },
        { title: "Storage Audit", value: "STORAGE_AUDIT" },
      ],
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    allSectionsHaveItems() {
      const sections = this.checklistItems.filter(
        (item) => item.type === "section"
      );

      if (!sections.length) {
        return true;
      }

      return sections.every(
        (section) => section.items && section.items.length > 0
      );
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal && this.checklist) {
        this.loadChecklistData();
      } else if (!newVal) {
        this.resetForm();
      }
    },
    checklist: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.modelValue) {
          this.loadChecklistData();
        }
      },
    },
  },
  methods: {
    loadChecklistData() {
      if (this.checklist) {
        this.formData = {
          checklist_name: this.checklist.checklist_name || "",
          checklist_type: this.checklist.checklist_type || "",
          checklist_source: this.checklist.checklist_source || "BMS",
        };

        if (this.checklist.checklist_sections) {
          try {
            const sections =
              typeof this.checklist.checklist_sections === "string"
                ? JSON.parse(this.checklist.checklist_sections)
                : this.checklist.checklist_sections;

            this.checklistItems = sections.map((item, index) => ({
              id: Date.now() + index,
              ...item,
            }));
          } catch (e) {
            console.error("Parse error", e);
            this.checklistItems = [];
          }
        }
      }
    },
    addLineItem() {
      if (this.lineItemName.trim()) {
        this.checklistItems.push({
          id: Date.now(),
          type: "line_item",
          name: this.lineItemName.trim(),
        });
        this.lineItemName = "";
      }
    },
    editLineItem(index) {
      if (
        this.editingLineItemIndex !== null &&
        this.editingLineItemIndex !== index
      ) {
        const prev = this.editingLineItemIndex;

        if (this.editingLineItemName.trim()) {
          this.checklistItems[prev].name = this.editingLineItemName.trim();
        }
      }

      this.editingLineItemIndex = index;
      this.editingLineItemName = this.checklistItems[index].name;
    },
    saveLineItemEdit(index) {
      if (this.editingLineItemName.trim()) {
        this.checklistItems[index].name = this.editingLineItemName.trim();
      }

      this.cancelLineItemEdit();
    },
    cancelLineItemEdit() {
      this.editingLineItemIndex = null;
      this.editingLineItemName = "";
    },
    removeLineItem(index) {
      this.checklistItems.splice(index, 1);
      if (this.editingLineItemIndex === index) {
        this.cancelLineItemEdit();
      } else if (this.editingLineItemIndex > index) {
        this.editingLineItemIndex--;
      }
    },
    addTempSectionItem() {
      if (this.tempSectionItemName.trim()) {
        const itemWeightage = this.tempSectionItems.length;
        this.tempSectionItems.push({
          name: this.tempSectionItemName.trim(),
          weightage: itemWeightage,
        });
        this.tempSectionItemName = "";
      }
    },
    removeTempSectionItem(index) {
      this.tempSectionItems.splice(index, 1);
      // Re-index weightages after removal
      this.tempSectionItems.forEach((item, idx) => {
        item.weightage = idx;
      });
    },
    addSection() {
      if (this.sectionName.trim() && this.tempSectionItems.length > 0) {
        this.checklistItems.push({
          id: Date.now(),
          type: "section",
          section_name: this.sectionName.trim(),
          items: [...this.tempSectionItems],
        });

        this.sectionName = "";
        this.tempSectionItemName = "";
        this.tempSectionItems = [];
      }
    },
 editSection(index) {

  // Save previous editing section if switching
  if (
    this.editingSectionIndex !== null &&
    this.editingSectionIndex !== index
  ) {

    const prev = this.editingSectionIndex;

    const prevSection = this.checklistItems[prev];

    if (
      prevSection &&
      prevSection.type === "section" &&
      this.editingSectionName.trim()
    ) {
      prevSection.section_name = this.editingSectionName.trim();
    }

  }

  const section = this.checklistItems[index];

  // Safety check
  if (!section || section.type !== "section") return;

  this.editingSectionIndex = index;
  this.editingSectionName = section.section_name;

},
    saveSectionEdit(index) {
      if (this.editingSectionName.trim()) {
        this.checklistItems[index].section_name =
          this.editingSectionName.trim();
      }

      this.cancelSectionEdit();
    },
    cancelSectionEdit() {
      this.editingSectionIndex = null;
      this.editingSectionName = "";
    },
    removeSection(index) {
      this.checklistItems.splice(index, 1);

      if (this.editingSectionIndex === index) {
        this.cancelSectionEdit();
      }
    },

    editSectionItem(sectionIndex, itemIndex) {
      if (
        this.editingSectionItemIndex !== null &&
        this.editingItemIndex !== null
      ) {
        const prevSection = this.editingSectionItemIndex;
        const prevItem = this.editingItemIndex;

        const section = this.checklistItems[prevSection];

        if (
          section &&
          section.type === "section" &&
          section.items &&
          section.items[prevItem]
        ) {
          if (this.editingItemName.trim()) {
            section.items[prevItem].name = this.editingItemName.trim();
          }
        }
      }

      this.editingSectionItemIndex = sectionIndex;
      this.editingItemIndex = itemIndex;

      this.editingItemName =
        this.checklistItems[sectionIndex].items[itemIndex].name;
    },
    saveSectionItemEdit(sectionIndex, itemIndex) {
      if (this.editingItemName.trim()) {
        this.checklistItems[sectionIndex].items[itemIndex].name =
          this.editingItemName.trim();
      }

      this.cancelSectionItemEdit();
    },
    cancelSectionItemEdit() {
      this.editingSectionItemIndex = null;
      this.editingItemIndex = null;
      this.editingItemName = "";
    },
    removeItemFromSection(sectionIndex, itemIndex) {
      const section = this.checklistItems[sectionIndex];

      // Safety check
      if (!section || section.type !== "section") return;

      // Remove item
      section.items.splice(itemIndex, 1);

      // Recalculate weightage
      section.items.forEach((item, index) => {
        item.weightage = index;
      });

      // If currently editing this item, cancel edit
      if (
        this.editingSectionItemIndex === sectionIndex &&
        this.editingItemIndex === itemIndex
      ) {
        this.cancelSectionItemEdit();
      }

      // Adjust editing index if needed
      if (
        this.editingSectionItemIndex === sectionIndex &&
        this.editingItemIndex > itemIndex
      ) {
        this.editingItemIndex--;
      }
    },
    buildChecklistSections() {
      return this.checklistItems.map((item, index) => {
        if (item.type === "line_item") {
          return {
            type: "line_item",
            name: item.name,
            sort_weightage: index,
          };
        }

        if (item.type === "section") {
          return {
            type: "section",
            section_name: item.section_name,
            sort_weightage: index,
            items: item.items.map((i, idx) => ({
              name: i.name,
              weightage: idx,
            })),
          };
        }
      });
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        checklist_name: "",
        checklist_type: "",
        checklist_source: "BMS",
      };

      this.itemType = "line_item";
      this.lineItemName = "";

      this.sectionName = "";
      this.checklistItems = [];

      this.tempSectionItemName = "";
      this.tempSectionItems = [];

      this.editingLineItemIndex = null;
      this.editingLineItemName = "";

      this.editingSectionIndex = null;
      this.editingSectionName = "";

      this.editingSectionItemIndex = null;
      this.editingItemIndex = null;
      this.editingItemName = "";

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      if (!this.checklistItems.length) {
        this.snackbar = {
          show: true,
          message: "Please add at least one line item or section",
          color: "warning",
        };
        return;
      }

      // Validate that all sections have at least one item
      const sectionsWithoutItems = this.checklistItems
        .filter((item) => item.type === "section")
        .filter((section) => !section.items || section.items.length === 0);
      if (sectionsWithoutItems.length > 0) {
        this.snackbar = {
          show: true,
          message: "All sections must have at least one item",
          color: "warning",
        };
        return;
      }

      this.loading = true;

      try {
        const checklistSections = this.buildChecklistSections();

        // Ensure it's an array
        const sectionsArray = Array.isArray(checklistSections)
          ? checklistSections
          : Object.values(checklistSections);

        // Add sort_weightage to each object (0, 1, 2, 3...)
        const sectionsWithWeightage = sectionsArray.map((item, index) => ({
          ...item,
          sort_weightage: index,
        }));

        const input = {
          checklist_id: this.checklist.checklist_id || this.checklist.id || "",
          checklist_name: this.formData.checklist_name,
          checklist_type: this.formData.checklist_type,
          checklist_source: this.formData.checklist_source,
          checklist_sections: JSON.stringify(sectionsWithWeightage),
        };

        const response = await client.graphql({
          query: edit_master_checklists,
          variables: {
            input: input,
          },
        });

        const responseData = JSON.parse(response.data.edit_master_checklists);

        if (responseData.status == "SUCCESS") {
          this.loading = false;
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Checklist updated successfully",
            color: "success",
          };
          this.$emit("updated", 0);
          this.handleClose();
        } else {
          this.loading = false;
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to update checklist",
            color: "error",
          };
        }
      } catch (error) {
        this.loading = false;
        console.error("Error updating checklist:", error);
        this.snackbar = {
          show: true,
          message:
            error.message || "An error occurred while updating the checklist",
          color: "error",
        };
      }
    },
  },
};
</script>

<style scoped>
</style>

